// src/features/excursions/components/ExcursionRow.jsx
import React, { useState, useEffect } from "react";
import { TableCell, TableRow } from "components/ui/table";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import {
  Edit,
  Trash2,
  Package,
  MapPin,
  Bus,
  Ticket,
  Users,
  Phone,
  MoreHorizontal,
  CheckCircle,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";

const getInitials = (name) => {
  if (!name) return "?";
  const names = name.trim().split(/\s+/);

  if (names.length === 0) return "?";
  if (names.length === 1) return names[0].substring(0, 2).toUpperCase();

  return (names[0][0] + names[names.length - 1][0]).toUpperCase();
};

// ✅ Função auxiliar para formatar camarotes (Igual ao Card)
const formatCamarotes = (camaroteData) => {
  if (!camaroteData) return "";

  // Caso 1: Array de números (Novo padrão)
  if (
    camaroteData.numbers &&
    Array.isArray(camaroteData.numbers) &&
    camaroteData.numbers.length > 0
  ) {
    return camaroteData.numbers
      .map((num) => String(num).padStart(2, "0"))
      .join(", ");
  }

  // Caso 2: Número único (Legado)
  if (camaroteData.number) {
    return String(camaroteData.number).padStart(2, "0");
  }

  return "";
};

function ExcursionRow({
  excursion,
  index,
  onMarkPresent,
  onUpdateLunchInfo,
  onEdit,
  onDelete,
  onUpdateExcursion,
  canEdit,
}) {
  const [localLunchInfo, setLocalLunchInfo] = useState(
    excursion.lunchInfo || ""
  );

  useEffect(() => {
    setLocalLunchInfo(excursion.lunchInfo || "");
  }, [excursion.lunchInfo]);

  const handleInputChange = (e) => setLocalLunchInfo(e.target.value);

  const handleInputBlur = () => {
    if (localLunchInfo !== excursion.lunchInfo) {
      onUpdateLunchInfo(excursion.id, localLunchInfo);
    }
  };

  const statusOptions = ["Agendado", "Confirmado", "Cancelado"];

  const formatCurrency = (value) => {
    if (!value) return "-";
    if (typeof value === "number") {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value);
    }
    if (typeof value === "string" && value.includes("R$")) return value;
    const numericValue = Number(value);
    if (isNaN(numericValue)) return "-";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numericValue);
  };

  // Lógica de Estilo da Linha
  const currentStatus = excursion.status ? excursion.status.trim() : "";
  const isGreenState =
    currentStatus === "Confirmado" || excursion.presenceStatus === "Presente";

  let rowStyleClass = "border-l-4 border-l-transparent hover:border-l-gray-300";

  if (isGreenState) {
    rowStyleClass =
      "shadow-[inset_4px_0_0_0_#22c55e] bg-green-50/30 dark:bg-green-900/10";
  } else if (currentStatus === "Cancelado") {
    rowStyleClass = "shadow-[inset_4px_0_0_0_#ef4444] opacity-60 bg-red-50/10";
  } else {
    rowStyleClass =
      "hover:shadow-[inset_4px_0_0_0_#d1d5db] dark:hover:shadow-[inset_4px_0_0_0_#4b5563]";
  }

  // Prepara a string de exibição dos camarotes
  const camaroteDisplay = formatCamarotes(excursion.camarote);

  return (
    <TableRow className={`transition-all ${rowStyleClass}`}>
      {/* 1. ÍNDICE */}
      <TableCell className="text-center font-mono text-xs text-muted-foreground w-[40px] px-2">
        {String(index + 1).padStart(2, "0")}
      </TableCell>

      {/* 2. CLIENTE */}
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold border border-primary/20 shadow-sm flex-shrink-0">
            {getInitials(excursion.clientName)}
          </div>
          <div className="flex flex-col min-w-0">
            <span
              className="font-semibold text-sm leading-none truncate max-w-[200px]"
              title={excursion.clientName}
            >
              {excursion.clientName || "Sem Nome"}
            </span>
            {excursion.clientPhone && (
              <span className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1">
                <Phone className="h-3 w-3" /> {excursion.clientPhone}
              </span>
            )}
          </div>
        </div>
      </TableCell>

      {/* 3. ORIGEM E PACOTE */}
      <TableCell>
        <div className="flex flex-col items-start gap-1">
          <div className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            {excursion.city || "-"}
          </div>
          {excursion.comboIncluded && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              <Package className="h-3 w-3" /> Pacote
            </span>
          )}
        </div>
      </TableCell>

      {/* 4. QUANTIDADE E VEÍCULO */}
      <TableCell>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 font-medium text-sm">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            {excursion.quantity || 0}
          </div>
          {excursion.vehicle && (
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Bus className="h-3 w-3" />
              <span className="truncate max-w-[80px]" title={excursion.vehicle}>
                {excursion.vehicle}
              </span>
            </div>
          )}
        </div>
      </TableCell>

      {/* 5. PAGAMENTO */}
      <TableCell className="text-center">
        <span
          className={`font-mono font-bold text-sm ${
            excursion.paymentInfo
              ? "text-emerald-600 dark:text-emerald-300"
              : "text-muted-foreground"
          }`}
        >
          {formatCurrency(excursion.paymentInfo)}
        </span>
      </TableCell>

      {/* 6. CAMAROTE (ATUALIZADO) */}
      <TableCell className="text-center">
        {excursion.camarote?.requested ? (
          <div className="flex flex-col items-center justify-center">
            <div className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 p-1.5 rounded-md">
              <Ticket className="h-4 w-4" />
            </div>
            {/* ✅ Exibe a string formatada (ex: "01, 02") */}
            {camaroteDisplay && (
              <span className="text-[11px] font-bold mt-0.5 text-amber-700 dark:text-amber-400 max-w-[80px] leading-tight">
                Nº {camaroteDisplay}
              </span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground/30">-</span>
        )}
      </TableCell>

      {/* 7. ALMOÇO */}
      <TableCell>
        <Input
          type="text"
          value={localLunchInfo}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder="Infos..."
          className="h-8 text-xs min-w-[120px] bg-secondary/40 border border-input/20 hover:bg-secondary/60 hover:border-input/50 focus:bg-background focus:border-primary transition-all text-center placeholder:text-muted-foreground/50 rounded-md"
          disabled={!canEdit}
        />
      </TableCell>

      {/* 8. STATUS E PRESENÇA */}
      <TableCell>
        {excursion.presenceStatus === "Presente" ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 ring-1 ring-inset ring-green-600/20">
            <span className="w-1.5 h-1.5 rounded-full bg-green-600 mr-1.5"></span>
            Presente
          </span>
        ) : (
          <Select
            value={currentStatus}
            onValueChange={(newStatus) =>
              onUpdateExcursion(excursion.id, { status: newStatus })
            }
            disabled={!canEdit}
          >
            <SelectTrigger
              className={`h-8 text-xs w-[95px] border-0 ring-1 ring-inset shadow-sm ${
                currentStatus === "Confirmado"
                  ? "ring-green-600/20 text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400"
                  : currentStatus === "Cancelado"
                  ? "ring-red-600/20 text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-400"
                  : "ring-gray-300 text-gray-600 dark:text-gray-400"
              }`}
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </TableCell>

      {/* 9. AÇÕES */}
      <TableCell className="text-right">
        {canEdit && (
          <div className="flex items-center justify-end gap-2">
            {/* Botão "Marcar Presença" */}
            {excursion.presenceStatus === "Ausente" && excursion.status !== "Cancelado" && (
              <Button
                onClick={() => onMarkPresent(excursion.id)}
                variant="outline"
                size="sm"
                className="h-8 text-[11px] px-2.5 border-dashed border-green-500 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                title="Confirmar Presença"
              >
                <CheckCircle className="mr-1.5 h-3.5 w-3.5" />
                Presença
              </Button>
            )}

            {/* Menu Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 w-8 p-0 hover:bg-muted rounded-full"
                >
                  <span className="sr-only">Abrir menu</span>
                  <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Ações</DropdownMenuLabel>
                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={onEdit}>
                  <Edit className="mr-2 h-4 w-4" />
                  <span>Editar</span>
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-destructive focus:text-destructive focus:bg-destructive/10"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  <span>Excluir</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

export default ExcursionRow;
